import { useCallback, useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Send, Download, Inbox, Loader2, Server } from "lucide-react";

const DOC_OPTIONS = [
  { id: "ipd_admission", label: "Admission" },
  { id: "billing",       label: "Billing" },
  { id: "discharge",     label: "Discharge" },
  { id: "prescription",  label: "Prescription" },
  { id: "lab",           label: "Lab" },
  { id: "imaging",       label: "Imaging" },
];

/**
 * ABDMGatewayInboxModal — M3 cross-hospital record sharing surface.
 *
 * Two tabs:
 *   • **Requests / Inbox** — documents shared *with* this hospital by
 *     other HIPs. Each row exposes a Download action that decrypts the
 *     Fernet envelope via `POST /abdm/share/{id}/fetch`.
 *   • **Send request** — outbound record request; asks another HIP or
 *     the gateway to release documents for a given ABHA / health-id,
 *     backed optionally by an approved consent artefact.
 */
export default function ABDMGatewayInboxModal({ open, onClose }) {
  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  // Send-request form
  const [reqHealthId, setReqHealthId] = useState("");
  const [reqTarget, setReqTarget] = useState("");
  const [reqConsentId, setReqConsentId] = useState("");
  const [reqDocs, setReqDocs] = useState(["discharge"]);

  const loadInbox = useCallback(async () => {
    setLoading(true);
    try {
      const [inb, out] = await Promise.all([
        api.get("/abdm/share/inbox"),
        api.get("/abdm/gateway/record-requests"),
      ]);
      setInbox(inb.data.rows || []);
      setOutbox(out.data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) loadInbox(); }, [open, loadInbox]);

  const fetchShare = async (row) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/abdm/share/${row.id}/fetch`);
      // Download the decrypted payload
      const blob = new Blob([JSON.stringify(data.payload, null, 2)],
        { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `abdm_share_${row.id.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Payload decrypted and downloaded");
      loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fetch failed");
    } finally { setBusyId(""); }
  };

  const toggleDoc = (id) =>
    setReqDocs((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const sendRequest = async (e) => {
    e.preventDefault();
    if (!reqHealthId.trim()) return toast.error("Enter an ABHA / Health-ID");
    setBusyId("send");
    try {
      await api.post("/abdm/gateway/request-record", {
        health_id: reqHealthId.trim(),
        target_hospital_id: reqTarget.trim() || null,
        consent_id: reqConsentId.trim() || null,
        document_types: reqDocs,
      });
      toast.success("Record request sent");
      setReqHealthId(""); setReqTarget(""); setReqConsentId("");
      setTab("inbox");
      loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setBusyId(""); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="abdm-gateway-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server size={18} className="text-emerald-600" />
            ABDM Gateway
            <Button size="sm" variant="ghost" onClick={loadInbox}
                    className="ml-auto"
                    data-testid="abdm-gateway-refresh">
              <RefreshCw size={14} />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="inbox" data-testid="abdm-gateway-tab-inbox">
              <Inbox size={13} className="mr-1" /> Requests ({inbox.length + outbox.length})
            </TabsTrigger>
            <TabsTrigger value="send" data-testid="abdm-gateway-tab-send">
              <Send size={13} className="mr-1" /> Send request
            </TabsTrigger>
          </TabsList>

          {/* ---------- Inbox tab ---------- */}
          <TabsContent value="inbox" className="mt-3 space-y-3 max-h-[55vh] overflow-y-auto">
            {loading ? (
              <div className="text-center py-6 text-slate-400">
                <Loader2 className="mx-auto animate-spin mb-1" size={16} /> Loading…
              </div>
            ) : (
              <>
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                    Shared with us
                  </p>
                  {inbox.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">No incoming shares yet.</p>
                  ) : inbox.map((r) => (
                    <div key={r.id}
                         className="flex items-center justify-between border rounded p-2 my-1 text-xs"
                         data-testid={`abdm-inbox-row-${r.id}`}>
                      <div>
                        <p className="font-medium">
                          From {r.source_hospital_id?.slice(0, 8)}…
                        </p>
                        <p className="text-slate-500">
                          {r.document_ids?.length || 0} document(s) ·
                          <Badge className="ml-1 bg-slate-100 text-slate-600 border-transparent">
                            {r.status}
                          </Badge>
                          · {new Date(r.shared_at).toLocaleDateString("en-IN")}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" disabled={busyId === r.id}
                              onClick={() => fetchShare(r)}
                              className="text-blue-700 border-blue-200"
                              data-testid={`abdm-inbox-fetch-${r.id}`}>
                        {busyId === r.id
                          ? <Loader2 className="animate-spin" size={12} />
                          : <><Download size={12} className="mr-1" /> Fetch</>}
                      </Button>
                    </div>
                  ))}
                </div>

                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-1 mt-3">
                    Our outgoing requests
                  </p>
                  {outbox.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">No outgoing requests yet.</p>
                  ) : outbox.map((r) => (
                    <div key={r.id}
                         className="flex items-center justify-between border rounded p-2 my-1 text-xs"
                         data-testid={`abdm-outbox-row-${r.id}`}>
                      <div>
                        <p className="font-medium">
                          {r.health_id}
                          {r.patient_name && ` · ${r.patient_name}`}
                        </p>
                        <p className="text-slate-500">
                          {r.document_types?.join(", ") || "any"} ·
                          <Badge className="ml-1 bg-amber-100 text-amber-800 border-transparent">
                            {r.status}
                          </Badge>
                          · {new Date(r.requested_at).toLocaleDateString("en-IN")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* ---------- Send tab ---------- */}
          <TabsContent value="send" className="mt-3">
            <form onSubmit={sendRequest} className="space-y-3">
              <div>
                <Label className="text-xs">ABHA / Health-ID *</Label>
                <Input value={reqHealthId} onChange={(e) => setReqHealthId(e.target.value)}
                       placeholder="e.g. ram@sbx or 91-1234-…"
                       data-testid="abdm-send-health-id" />
              </div>
              <div>
                <Label className="text-xs">Target hospital id (optional)</Label>
                <Input value={reqTarget} onChange={(e) => setReqTarget(e.target.value)}
                       placeholder="Leave blank to route via gateway"
                       className="font-mono text-xs"
                       data-testid="abdm-send-target" />
              </div>
              <div>
                <Label className="text-xs">Consent id (optional but recommended)</Label>
                <Input value={reqConsentId} onChange={(e) => setReqConsentId(e.target.value)}
                       placeholder="Approved consent id backing this request"
                       className="font-mono text-xs"
                       data-testid="abdm-send-consent" />
              </div>
              <div>
                <Label className="text-xs">Document types</Label>
                <div className="grid grid-cols-3 gap-1 mt-1">
                  {DOC_OPTIONS.map((d) => {
                    const on = reqDocs.includes(d.id);
                    return (
                      <button type="button" key={d.id} onClick={() => toggleDoc(d.id)}
                              data-testid={`abdm-send-doc-${d.id}`}
                              className={`text-xs px-2 py-1 rounded border ${
                                on ? "bg-blue-50 border-blue-300 text-blue-800"
                                    : "bg-white border-slate-200 text-slate-500"
                              }`}>
                        {on ? "✓" : "◯"} {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button type="submit" disabled={busyId === "send"}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="abdm-send-submit">
                {busyId === "send"
                  ? <Loader2 className="animate-spin mr-1" size={14} />
                  : <Send size={14} className="mr-1" />}
                Request records
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
